import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import { cn } from "../lib/cn";

interface NetworkSnapshot {
  hostname: string;
  interfaces: NetworkInterface[];
}
interface NetworkInterface {
  name: string;
  mac?: string;
  up: boolean;
  loopback: boolean;
  // Defensive — older server builds (or any client-side filter) may
  // return null/undefined here. The component coerces to [] before reading.
  addresses?: string[] | null;
}

// NetworkCard answers operators' first triage question when a PLC isn't
// reachable: "is this an agent problem or a network problem?" It's pure
// introspection — no actions, just the truth about which interfaces the
// agent process can see.
export function NetworkCard() {
  const network = useQuery({
    queryKey: ["network"],
    queryFn: () => api.get<NetworkSnapshot>("/api/v1/network"),
    staleTime: 60_000,
  });

  return (
    <section className="card overflow-hidden">
      <div className="border-b border-rule px-6 py-5">
        <h2 className="font-display text-[20px] font-semibold tracking-tight text-ink">
          Network
        </h2>
        <p className="mt-2 max-w-xl text-[13.5px] leading-relaxed text-ash">
          IPs this agent can bind to and originate connections from.
          Plant-floor PLCs and SCADA must be reachable on one of these.
        </p>
      </div>

      {network.isLoading ? (
        <p className="px-6 py-8 text-sm text-ash">Loading…</p>
      ) : null}

      {network.data ? (
        <>
          <div className="grid grid-cols-[160px_1fr] gap-4 border-b border-rule px-6 py-3.5">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
              Hostname
            </div>
            <div className="font-mono text-[13px] text-ink">
              {network.data.hostname || <span className="text-ash">—</span>}
            </div>
          </div>

          <ul className="divide-y divide-rule">
            {network.data.interfaces.map((iface) => (
              <li key={iface.name} className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="font-mono text-[13px] font-semibold text-ink">
                    {iface.name}
                  </div>
                  <StatusChip iface={iface} />
                  {iface.mac ? (
                    <span className="font-mono text-[10.5px] text-ash">
                      {iface.mac}
                    </span>
                  ) : null}
                </div>
                {(iface.addresses?.length ?? 0) === 0 ? (
                  <div className="mt-1.5 text-[12px] italic text-ash">
                    No addresses bound
                  </div>
                ) : (
                  <ul className="mt-1.5 flex flex-wrap gap-1.5">
                    {(iface.addresses ?? []).map((addr) => (
                      <li
                        key={addr}
                        className={cn(
                          "rounded border px-1.5 py-0.5 font-mono text-[11px]",
                          iface.loopback
                            ? "border-rule bg-paper text-ash"
                            : "border-brand-500/30 bg-brand-50 text-brand-700",
                        )}
                      >
                        {addr}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}

function StatusChip({ iface }: { iface: NetworkInterface }) {
  if (iface.loopback) return <span className="chip-neutral">Loopback</span>;
  if (iface.up) {
    return (
      <span className="chip-emerald">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Up
      </span>
    );
  }
  return <span className="chip-rose">Down</span>;
}
